import { useEffect, useMemo, useState } from "react";
import Joi from "joi-browser";

import { useForm } from "V2.0/HomePages/Assistants/AssistantTools/CustomAPIs/components/Form/useForm";
import Form from "V2.0/HomePages/Assistants/AssistantTools/CustomAPIs/components/Form";
import { Button } from "V2.0/common/button";

import { getModelOptions } from "../../ModelSettings/GPTResponseModelCategorized/GPTResponseModelSettings";
import { reasoningModels } from "../../ModelSettings/GPTResponseModelCategorized/gptModels";
import { message } from "antd";
import { showErrorToast } from "V2.0/utils";
import { maskGptResponseModel } from "../../ModelSettings/GPTResponseModelCategorized/GPTResponsePremiumModel";

export default AssistantCoachModel;

const modelOptions = getModelOptions(reasoningModels);

const coachModelField = {
  name: "coachModel",
  label: "Pick Model",
  type: "select",
  options: modelOptions,
};

const temperatureField = {
  name: "temperature",
  label: "Temperature",
  type: "slider",
  min: 0,
  max: 1,
  step: 0.1,
};

const maxOutputTokensField = {
  name: "maxOutputTokens",
  label: "Maximum Output Token",
  type: "slider",
  min: 100,
  step: 100,
  max: 10000,
};

const template = [coachModelField, temperatureField, maxOutputTokensField];

const schema = {
  coachModel: Joi.string().required().label("Model"),
  maxOutputTokens: Joi.label("Maximum Output Tokens"),
  temperature: Joi.label("Temperature"),
};

function getDefaultAssistantCoachModel(modelSettings) {
  return {
    coachModel: maskGptResponseModel(
      modelSettings?.coachModel ?? "gemini-2.5-flash",
    ),
    maxOutputTokens: modelSettings?.maxOutputTokens ?? 1000,
    temperature: modelSettings?.temperature ?? 0.3,
  };
}

function AssistantCoachModel(props) {
  const { assistantCoach, onUpdateAssistant, isUnlocked } = props;

  const [inputField] = useState(() =>
    getDefaultAssistantCoachModel(assistantCoach?.modelSettings ?? {}),
  );

  const { data, error, handleChange } = useForm(inputField, schema);

  const handleSave = () => {
    const payload = {
      modelSettings: data,
    };

    const lifecycleCbs = {
      init() {
        message.loading("Updating Model Settings");
      },
      success() {
        message.destroy();
        message.success("Model Settings Updated");
      },
      error(e) {
        message.destroy();
        showErrorToast(e, "Failed to update model Settings");
      },
    };

    onUpdateAssistant(payload, lifecycleCbs);
  };

  const updatedTemplate = useMemo(() => {
    return template.map((field) => {
      return {
        ...field,
        disabled: !isUnlocked,
      };
    });
  }, [isUnlocked]);

  const coachModelUpdatedTemplate = useMemo(() => {
    const coachModel = maskGptResponseModel(
      assistantCoach?.modelSettings?.coachModel,
    );

    const isCoachModelPartOfReasoningModel =
      coachModel && Boolean(reasoningModels[coachModel]);

    if (!isCoachModelPartOfReasoningModel) {
      const updatedModelList = { ...reasoningModels, [coachModel]: coachModel };

      const updatedCoachModelField = {
        ...updatedTemplate[0],
        options: getModelOptions(updatedModelList),
      };

      const updatedTemplateWithModelOptions = updatedTemplate.toSpliced(
        0,
        1,
        updatedCoachModelField,
      );

      return updatedTemplateWithModelOptions;
    }

    return updatedTemplate;
  }, [assistantCoach?.modelSettings?.coachModel, updatedTemplate]);

  return (
    <div className="w-2/4 p-4">
      <div className="flex flex-col border rounded-lg space-y-4 h-full">
        <div className="px-6 py-4 bg-gray-50 rounded-t-lg flex items-center gap-x-2">
          <p className="text-md font-semibold text-gray-900">Model</p>
        </div>
        <div className="px-8">
          <Form
            data={data}
            error={error}
            template={coachModelUpdatedTemplate}
            handleChange={handleChange}
          />
        </div>
        {isUnlocked && (
          <div className="bg-gray-50 text-right rounded-b-lg px-6 py-3">
            <Button onClick={handleSave}>Save</Button>
          </div>
        )}
      </div>
    </div>
  );
}
